import React, {
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useReactMediaRecorder } from "react-media-recorder";
import i18n from "@emoji-mart/data/i18n/pt.json";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import {
  Box,
  CircularProgress,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  TextField,
  useTheme,
} from "@mui/material";
import {
  Send as SendIcon,
  Paperclip as AnexoIcon,
  Image as ImageIcon,
  Video as VideoIcon,
  Headphones as AudioIcon,
  FileDocument as DocumentIcon,
  Microphone as MicrophoneIcon,
  Camera as CameraIcon,
  Emoticon as EmoticonIcon,
  Pause as PauseIcon,
  TrashCanOutline as TrashIcon,
  VideoPlus as VideoPlusIcon,
} from "mdi-material-ui";
import { EChatType } from "@deltasge/marauders-map";
import { isMobile } from "react-device-detect";

import { useFFmpegAudio } from "hooks/useFFmpegAudio";
import { useAppSelector } from "store";
import { Input } from "assets/styleds";

interface IAudioVisualizer {
  animationFrameId: number | null;
  audioContext: AudioContext | null;
  analyser: AnalyserNode | null;
  source: MediaStreamAudioSourceNode | null;
}

export const ChatTextFieldComponent: FC<{
  message: string;
  setMessage: (value: SetStateAction<string>) => void;
  handleSendMsg: () => void;
  disabled?: boolean;
  handleAttachment: (args: {
    file: File;
    chatType: EChatType;
  }) => Promise<void>;
  anexando: boolean;
}> = ({
  anexando,
  handleAttachment,
  handleSendMsg,
  message,
  setMessage,
  disabled,
}) => {
  const audioVisualizerCanvasRef = useRef<HTMLCanvasElement>(null);
  const audioVisualizerRef = useRef<IAudioVisualizer>({
    animationFrameId: null,
    audioContext: null,
    analyser: null,
    source: null,
  });
  const [audioChunks, setAudioChunks] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>();
  const [audioStart, setAudioStart] = useState(false);
  const [audioSave, setAudioSave] = useState(false);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);

  const [anchorEmoticon, setAnchorEmoticon] =
    useState<HTMLButtonElement | null>(null);
  const [anchorMenu, setAnchorMenu] = useState<HTMLElement | null>(null);

  const {
    layout: {
      theme: { mode },
    },
    chat: { options },
  } = useAppSelector((state) => state);

  const theme = useTheme();

  const { isReady, mergeBlobsToMp3 } = useFFmpegAudio();

  const {
    status: audioStatus,
    startRecording: startAudioRecording,
    stopRecording: stopAudioRecording,
    clearBlobUrl: clearAudioBlobUrl,
    previewAudioStream,
  } = useReactMediaRecorder({
    audio: true,
    video: false,
    onStop: async (_, blob) => {
      if (blob.size > 0) {
        const blobs: Blob[] = [];
        if (audioChunks) blobs.push(audioChunks);
        blobs.push(blob);
        const result = await mergeBlobsToMp3(blobs);
        setAudioChunks(result);
      }
      setIsProcessingAudio(false);
    },
  });

  const handleAudioStart = useCallback(() => {
    clearAudioBlobUrl();
    setAudioChunks(null);
    setAudioUrl(undefined);
    setAudioStart(true);
    startAudioRecording();
  }, [clearAudioBlobUrl, startAudioRecording]);

  const handleAudioTrash = useCallback(() => {
    setAudioStart(false);
    stopAudioRecording();
    clearAudioBlobUrl();
    setAudioChunks(null);
    setAudioUrl(undefined);
    setIsProcessingAudio(false);
    setAudioSave(false);
  }, [clearAudioBlobUrl, stopAudioRecording]);

  const handleAudioResume = useCallback(() => {
    startAudioRecording();
    setAudioUrl(undefined);
  }, [startAudioRecording]);

  const handleAudioPause = useCallback(() => {
    stopAudioRecording();
    clearAudioBlobUrl();
  }, [clearAudioBlobUrl, stopAudioRecording]);

  const handleAudioFinish = useCallback(async () => {
    setIsProcessingAudio(true);
    stopAudioRecording();
    clearAudioBlobUrl();
    setAudioUrl(undefined);
    setAudioStart(false);
    setAudioSave(true);
  }, [clearAudioBlobUrl, stopAudioRecording]);

  useEffect(() => {
    (async () => {
      if (audioSave && audioChunks && !anexando && !isProcessingAudio) {
        const file = new File([audioChunks], `audio-${Date.now()}.mp3`, {
          type: "audio/mpeg",
        });
        await handleAttachment({ file, chatType: EChatType.Audio });
        setAudioChunks(null);
        setAudioSave(false);
      }
    })();
  }, [audioSave, audioChunks, handleAttachment, anexando, isProcessingAudio]);

  useEffect(() => {
    let url: string | undefined = undefined;
    if (audioChunks && audioChunks.size > 0 && audioStart) {
      url = URL.createObjectURL(audioChunks);
      setAudioUrl(url);
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [audioChunks, audioStart]);

  useEffect(() => {
    const visualizer = audioVisualizerRef.current;
    const canvas = audioVisualizerCanvasRef.current;

    // Função que desenha a forma de onda no canvas
    const drawWaveform = () => {
      if (!visualizer.analyser || !canvas) return;

      // Agenda o próximo quadro da animação
      visualizer.animationFrameId = requestAnimationFrame(drawWaveform);

      const bufferLength = visualizer.analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      visualizer.analyser.getByteTimeDomainData(dataArray); // Obtém os dados da onda

      const canvasCtx = canvas.getContext("2d");
      if (!canvasCtx) return;

      // Limpa o canvas e define os estilos
      canvasCtx.fillStyle = theme.palette.background.default;
      canvasCtx.fillRect(0, 0, canvas.width, canvas.height);
      canvasCtx.lineWidth = 2;
      canvasCtx.strokeStyle = theme.palette.primary.main;
      canvasCtx.beginPath();

      const sliceWidth = (canvas.width * 1.0) / bufferLength;
      let x = 0;

      // Desenha a linha da onda
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * canvas.height) / 2;
        i === 0 ? canvasCtx.moveTo(x, y) : canvasCtx.lineTo(x, y);
        x += sliceWidth;
      }

      canvasCtx.lineTo(canvas.width, canvas.height / 2);
      canvasCtx.stroke();
    };

    if (audioStatus === "recording" && previewAudioStream && canvas) {
      // Ajusta as dimensões do canvas para evitar distorções no desenho.
      // O tamanho da área de desenho (atributos) deve ser igual ao tamanho de exibição (CSS).
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;

      // Garante que o stream de áudio possui uma faixa de áudio antes de usá-lo.
      if (previewAudioStream.getAudioTracks().length === 0) {
        // eslint-disable-next-line no-console
        console.error(
          "Visualizador de waveform: MediaStream não possui faixa de áudio."
        );
        return;
      }

      // Configura a Web Audio API
      visualizer.audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      visualizer.analyser = visualizer.audioContext.createAnalyser();
      visualizer.source =
        visualizer.audioContext.createMediaStreamSource(previewAudioStream);
      visualizer.source.connect(visualizer.analyser); // Conecta a fonte ao analisador
      drawWaveform(); // Inicia o desenho
    }

    // Função de limpeza para parar a animação e liberar os recursos
    return () => {
      if (visualizer.animationFrameId)
        cancelAnimationFrame(visualizer.animationFrameId);
      if (visualizer.source) visualizer.source.disconnect();
      if (visualizer.audioContext?.state !== "closed")
        visualizer.audioContext?.close();
    };
  }, [audioStatus, previewAudioStream, theme.palette]);

  const podeEnviarEmoji = options.liberarEmoji || false;
  const podeEnviarAnexo = options.liberarAnexo || false;
  const podeEnviarFoto = (options?.liberarFoto && isMobile) || false;
  const podeEnviarVideo = (options?.liberarVideo && isMobile) || false;
  const podeEnviarAudio =
    (options.liberarAudio && !message && isReady) || false;
  const podeEnviarMensagem = message || !isReady || !podeEnviarAudio;

  return (
    <Box>
      <Grid container>
        <Grid item xs>
          {audioStart && (
            <Grid container>
              <Grid item xs="auto">
                <IconButton onClick={handleAudioTrash}>
                  <TrashIcon />
                </IconButton>
              </Grid>
              <Grid item xs>
                {audioStatus === "recording" && (
                  <canvas
                    ref={audioVisualizerCanvasRef}
                    style={{ width: "100%", height: theme.spacing(5) }}
                  />
                )}
                {audioUrl && (
                  <audio
                    src={audioUrl}
                    controls
                    style={{ width: "100%", height: theme.spacing(5) }}
                  />
                )}
              </Grid>
              <Grid xs="auto">
                <IconButton
                  onClick={
                    audioStatus === "recording"
                      ? handleAudioPause
                      : handleAudioResume
                  }
                >
                  {audioStatus === "recording" && <PauseIcon />}
                  {audioStatus === "stopped" && <MicrophoneIcon />}
                </IconButton>
              </Grid>
            </Grid>
          )}
          {!audioStart && (
            <TextField
              placeholder="Mensagem..."
              value={message}
              onChange={({ target: { value } }) => setMessage(value)}
              onKeyDown={(e) => {
                if (e.key == "Enter") {
                  e.preventDefault();
                  handleSendMsg();
                }
              }}
              disabled={disabled}
              fullWidth
              InputProps={{
                startAdornment: podeEnviarEmoji ? (
                  <InputAdornment position="start" sx={{ mt: "0 !important" }}>
                    <IconButton
                      aria-label="emotion"
                      onClick={(e) => {
                        setAnchorEmoticon(e.currentTarget);
                      }}
                    >
                      <EmoticonIcon />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
                endAdornment:
                  podeEnviarAnexo || podeEnviarFoto ? (
                    <InputAdornment position="end">
                      {podeEnviarFoto && (
                        <IconButton component="label" role={undefined}>
                          <Input
                            accept="image/*"
                            capture="environment"
                            type="file"
                            onChange={async (event) => {
                              const file = event.target.files?.item(0);
                              if (!file) return;

                              await handleAttachment({
                                file,
                                chatType: EChatType.Image,
                              });
                              event.target.files = null;
                              setAnchorMenu(null);
                            }}
                          />
                          <CameraIcon />
                        </IconButton>
                      )}
                      {podeEnviarAnexo && (
                        <>
                          <IconButton
                            aria-label="attachment"
                            onClick={(e) => {
                              setAnchorMenu(e.currentTarget);
                            }}
                            disabled={anexando}
                          >
                            {anexando ? (
                              <CircularProgress
                                sx={({ spacing }) => ({
                                  width: spacing(3).concat(" !important"),
                                  height: spacing(3).concat(" !important"),
                                })}
                              />
                            ) : (
                              <AnexoIcon />
                            )}
                          </IconButton>
                          <Menu
                            anchorEl={anchorMenu}
                            open={Boolean(anchorMenu)}
                            onClose={() => setAnchorMenu(null)}
                          >
                            {podeEnviarVideo && (
                              <MenuItem component="label" role={undefined}>
                                <Input
                                  accept="video/*"
                                  capture="environment"
                                  type="file"
                                  onChange={async (event) => {
                                    const file = event.target.files?.item(0);
                                    if (!file) return;

                                    await handleAttachment({
                                      file,
                                      chatType: EChatType.Video,
                                    });
                                    event.target.files = null;
                                    setAnchorMenu(null);
                                  }}
                                />
                                <ListItemIcon>
                                  <VideoPlusIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Gravar vídeo</ListItemText>
                              </MenuItem>
                            )}
                            <MenuItem component="label" role={undefined}>
                              <Input
                                accept="image/*"
                                type="file"
                                onChange={async (event) => {
                                  const file = event.target.files?.item(0);
                                  if (!file) return;

                                  await handleAttachment({
                                    file,
                                    chatType: EChatType.Image,
                                  });
                                  event.target.files = null;
                                  setAnchorMenu(null);
                                }}
                              />
                              <ListItemIcon>
                                <ImageIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>Fotos</ListItemText>
                            </MenuItem>
                            <MenuItem component="label" role={undefined}>
                              <Input
                                accept="video/mp4,video/x-m4v,video/*"
                                type="file"
                                onChange={async (event) => {
                                  const file = event.target.files?.item(0);
                                  if (!file) return;
                                  await handleAttachment({
                                    file,
                                    chatType: EChatType.Video,
                                  });
                                  event.target.files = null;
                                  setAnchorMenu(null);
                                }}
                              />
                              <ListItemIcon>
                                <VideoIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>Vídeos</ListItemText>
                            </MenuItem>
                            <MenuItem component="label" role={undefined}>
                              <Input
                                accept="audio/mp3,audio/*;capture=microphone"
                                type="file"
                                onChange={async (event) => {
                                  const file = event.target.files?.item(0);
                                  if (!file) return;

                                  await handleAttachment({
                                    file,
                                    chatType: EChatType.Audio,
                                  });
                                  event.target.files = null;
                                  setAnchorMenu(null);
                                }}
                              />
                              <ListItemIcon>
                                <AudioIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>Áudio</ListItemText>
                            </MenuItem>
                            <MenuItem component="label" role={undefined}>
                              <Input
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                type="file"
                                onChange={async (event) => {
                                  const file = event.target.files?.item(0);
                                  if (!file) return;
                                  await handleAttachment({
                                    file,
                                    chatType: EChatType.Attachment,
                                  });
                                  event.target.files = null;
                                  setAnchorMenu(null);
                                }}
                              />
                              <ListItemIcon>
                                <DocumentIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>Documentos</ListItemText>
                            </MenuItem>
                          </Menu>
                        </>
                      )}
                    </InputAdornment>
                  ) : undefined,
              }}
              sx={{
                "& input": { paddingTop: 1.5, paddingBottom: 1.5 },
                "& .MuiInputBase-root": {
                  pl: 0,
                  pr: 0,
                },
              }}
            />
          )}
        </Grid>
        <Grid item xs="auto">
          {podeEnviarMensagem && (
            <Fab
              color="secondary"
              sx={({ spacing }) => ({
                position: "relative",
                top: spacing(-0.5),
                right: spacing(-0.5),
              })}
              onClick={handleSendMsg}
            >
              <SendIcon />
            </Fab>
          )}
          {podeEnviarAudio && (
            <Fab
              color="secondary"
              sx={({ spacing }) => ({
                position: "relative",
                top: spacing(-0.5),
                right: spacing(-0.5),
              })}
              onClick={!audioStart ? handleAudioStart : handleAudioFinish}
              disabled={disabled}
            >
              {audioStart ? <SendIcon /> : <MicrophoneIcon />}
            </Fab>
          )}
        </Grid>
      </Grid>
      <Popover
        open={Boolean(anchorEmoticon)}
        anchorEl={anchorEmoticon}
        onClose={() => setAnchorEmoticon(null)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Picker
          data={data}
          theme={mode}
          onEmojiSelect={(args: { native: string }) => {
            setMessage((prev) => prev + args.native);
          }}
          i18n={i18n}
          previewPosition="none"
          skinTonePosition="none"
        />
      </Popover>
    </Box>
  );
};
